import { DeleteOutlineOutlined } from "@mui/icons-material";
import { styled } from "@mui/material";
import React from "react";

import { Autocomplete } from "@/Components/v2/atoms/Autocomplete";
import { Box } from "@/Components/v2/atoms/Box";
import { IconButton } from "@/Components/v2/atoms/Button";
import { Checkbox } from "@/Components/v2/atoms/Checkbox";
import { Stack } from "@/Components/v2/atoms/Stack";
import { RenderInput, SelectLabel } from "@/Components/v2/molecule/LabelledAutoComplete/v2/shared";
import { MultiSelectProps, OptionV2 } from "@/Components/v2/molecule/LabelledAutoComplete/v2/types";

const DefaultWidth = 280;

type DocumentsSelectProps = Omit<MultiSelectProps, "allOptionsLabel" | "disableAllOption"> & {
  onDocumentArchived: (documentId: string) => void;
};

const StyledDeleteIcon = styled(DeleteOutlineOutlined)(({ theme }) => ({
  color: theme.palette.primary.main,
}));

export const DocumentsSelect = ({
  label,
  id,
  options,
  value,
  onChange,
  onDocumentArchived,
  width = DefaultWidth,
  disabled = false,
  disableClearable = false,
  placeholderOverride,
}: DocumentsSelectProps) => {
  const selectedOptions = options.filter((opt) => value.includes(opt.value));

  const handleChange = (_: unknown, selected: OptionV2[]) => {
    if (!selected) {
      onChange([]);
    } else {
      onChange(selected.map((opt) => opt.value));
    }
  };

  const getSelectedLabel = () => {
    if (!value.length) return undefined;
    return value.length === 1
      ? options.find((opt) => opt.value === value[0])?.label
      : `${value.length} selected`;
  };

  const handleDocumentArchive =
    (documentId: string) => (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onDocumentArchived(documentId);
    };

  return (
    <Stack flexDirection="row" alignItems="center">
      <SelectLabel label={label} />
      <Autocomplete
        multiple
        id={id}
        options={options}
        value={selectedOptions}
        onChange={handleChange}
        disabled={disabled}
        disableClearable={disableClearable}
        disableCloseOnSelect
        getOptionLabel={(option: OptionV2) => option.label}
        isOptionEqualToValue={(option: OptionV2, value: OptionV2) => option.value === value.value}
        renderOption={(
          props: React.HTMLAttributes<HTMLLIElement>,
          option: OptionV2,
          { selected }: { selected: boolean }
        ) => {
          return (
            <li {...props} key={option.value}>
              <Checkbox style={{ marginRight: 8 }} checked={selected} />
              <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                {option.label}
                <IconButton onClick={handleDocumentArchive(option.value)}>
                  <StyledDeleteIcon />
                </IconButton>
              </Box>
            </li>
          );
        }}
        style={{ width }}
        renderInput={(params: any) => (
          <RenderInput
            {...params}
            id={id}
            label={label}
            disabled={disabled}
            value={value}
            selectedLabel={getSelectedLabel()}
            placeholderOverride={placeholderOverride}
          />
        )}
        size="small"
      />
    </Stack>
  );
};
